import { useField, useForm } from "@taskfortress/core/src/form"
import {
  validatePassword,
  validatePasswordConfirmation,
} from "@taskfortress/core/src/form/validation"
import { useCallback, useState } from "react"
import { Alert } from "react-native"
import { useAuth } from "../auth"
import { BaseScreen, Button, Text, TextLink } from "../components"
import { AuthForm, AuthLayout, GenerateMasterKey } from "../components/auth"
import { TextInput } from "../components/form"

type ResetStep = "password" | "masterKey"

export const ResetPasswordScreen = ({ route, navigation }: any) => {
  const [currentStep, setCurrentStep] = useState<ResetStep>("password")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { resetAccount } = useAuth()

  // Get params passed from verification screen
  const { email, verificationToken, code } = route.params || {}

  // Form fields for step 1
  const passwordField = useField({
    name: "password",
    initialValue: "",
    validate: validatePassword,
  })

  const confirmPasswordField = useField({
    name: "confirmPassword",
    initialValue: "",
    validate: (value: string) =>
      validatePasswordConfirmation(value, {
        password: passwordField.value || "",
      }),
  })

  // Form management for step 1
  const form = useForm({
    fields: [passwordField, confirmPasswordField],
  })

  // Handle step 1: Validate password and proceed to master key generation
  const handleNextStep = useCallback(async () => {
    if (!form.valid) {
      Alert.alert("Error", "Please fix the errors in the form")
      return
    }

    setError(null)
    setCurrentStep("masterKey")
  }, [form.valid])

  // Handle step 2: Complete reset with generated master key
  const handleMasterKeyConfirmed = useCallback(
    async (masterKey: string) => {
      setError(null)
      setIsLoading(true)

      try {
        // Reset account with new password and master key
        const success = await resetAccount(
          email,
          verificationToken,
          code,
          passwordField.value!,
          masterKey,
        )

        if (success) {
          // Successfully reset and logged in
          // Auth provider will handle navigation to main app
        } else {
          throw new Error("Failed to reset your account. Please try again.")
        }
      } catch (err: any) {
        console.error("Reset error:", err)
        setError(err.message || "An error occurred during account reset.")
      } finally {
        setIsLoading(false)
      }
    },
    [email, verificationToken, code, passwordField.value, resetAccount],
  )

  // Handle going back to step 1
  const handleBackToPassword = useCallback(() => {
    setCurrentStep("password")
    setError(null)
  }, [])

  // Render step 1: Password input
  if (currentStep === "password") {
    return (
      <BaseScreen>
        <AuthLayout>
          <Text className="text-2xl font-bold mb-2 text-center">
            Reset Password
          </Text>
          <Text className="text-lg text-typography-500 mb-8 text-center">
            Create a new password
          </Text>

          <AuthForm onSubmit={handleNextStep}>
            <TextInput
              name="password"
              label="New Password"
              placeholder="New Password"
              value={passwordField.value}
              handleChange={passwordField.handleChange}
              error={passwordField.error}
              type="password"
              disabled={isLoading}
            />

            <TextInput
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Confirm Password"
              value={confirmPasswordField.value}
              handleChange={confirmPasswordField.handleChange}
              error={confirmPasswordField.error}
              type="password"
              disabled={isLoading}
            />

            <Button
              onPress={handleNextStep}
              disabled={isLoading || !form.valid}
              isLoading={isLoading}
            >
              Next: Generate Master Key
            </Button>

            <TextLink
              onPress={() => navigation.navigate("Login")}
              disabled={isLoading}
              className="text-center mx-auto"
            >
              Cancel and Return to Login
            </TextLink>
          </AuthForm>
        </AuthLayout>
      </BaseScreen>
    )
  }

  // Render step 2: Master key generation
  return (
    <BaseScreen>
      <AuthLayout>
        <Text className="text-2xl font-bold mb-2 text-center">
          Master Key Setup
        </Text>
        <Text className="text-lg text-typography-500 mb-8 text-center">
          Generate a new master key for your account
        </Text>

        <GenerateMasterKey
          onMasterKeyConfirmed={handleMasterKeyConfirmed}
          onBack={handleBackToPassword}
          isLoading={isLoading}
          error={error}
        />
      </AuthLayout>
    </BaseScreen>
  )
}
